import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { Calendar, Clock, User } from "lucide-react";
import { formatDateTime, formatDuration, libellesNiveauxQsort } from "@repo/utils";
import { QsortPyramide, type ItemPyramide } from "./QsortPyramide";

/** Réponse d'un item Q-sort : `niveau` est la donnée d'origine, `score` en est
 *  la projection dupliquée à la passation pour relire sans la pyramide.
 *  Échelle ipsative `nbNiveaux - niveau` : 5 (Essentiel) → 1 (Sans importance). */
interface FormReponseFitCulturel {
  name: string;
  label: string;
  niveau: number;
  score?: number;
}

interface FitCulturelDonnees {
  type: "form";
  template: "pc-fit";
  reponses: Record<string, FormReponseFitCulturel>;
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface FitCulturelResultsProps {
  donnees: FitCulturelDonnees;
  candidatInfo: CandidatInfo;
  /** Nombre de niveaux de la pyramide. 5 partout (`PYRAMIDE_FIT_CULTUREL`) ;
   *  paramétré ici uniquement pour ne pas figer une constante backend dans un
   *  paquet de rendu pur. */
  nbNiveaux?: number;
}

export function FitCulturelResults({
  donnees,
  candidatInfo,
  nbNiveaux = 5,
}: FitCulturelResultsProps) {
  if (donnees.type !== "form" || donnees.template !== "pc-fit") {
    return <div>Type de données incorrect</div>;
  }

  const reponses = Object.values(donnees.reponses).filter((r) =>
    Number.isInteger(r.niveau),
  );

  // Une passation stockée avec plus de niveaux que la pyramide courante reste
  // lisible : on élargit plutôt que de perdre des items en bas de tableau.
  const niveauMax = reponses.reduce((m, r) => Math.max(m, r.niveau), 0);
  const niveaux = Math.max(nbNiveaux, niveauMax + 1);
  const libelles = libellesNiveauxQsort(niveaux);

  const items: ItemPyramide[] = reponses.map((r) => ({
    code: r.name,
    label: r.label,
    niveau: r.niveau,
    valeur: r.score !== undefined ? String(r.score) : undefined,
  }));

  return (
    <div>
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">PC Fit</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Pyramide de priorités</CardTitle>
          <CardDescription>
            Classement du candidat, du plus essentiel (en haut) au sans
            importance (en bas). La distribution est forcée : le candidat a dû
            arbitrer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <QsortPyramide
            items={items}
            niveaux={niveaux}
            videMessage="Aucun item classé dans cette passation."
          />
        </CardContent>
      </Card>

      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Détail par item</CardTitle>
          <CardDescription>
            Niveau et score de chaque item classé — score ipsatif de {niveaux}{" "}
            (Essentiel) à 1 (Sans importance)
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Item</th>
                <th className="py-2 pr-4 font-medium">Niveau</th>
                <th className="py-2 font-medium">Score</th>
              </tr>
            </thead>
            <tbody>
              {[...reponses]
                .sort(
                  (a, b) =>
                    a.niveau - b.niveau || a.label.localeCompare(b.label, "fr"),
                )
                .map((item) => (
                  <tr key={item.name} className="border-b last:border-0">
                    <td className="py-2 pr-4">{item.label}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {libelles[item.niveau] ?? `Niveau ${item.niveau + 1}`}
                    </td>
                    <td className="py-2 tabular-nums">
                      {item.score !== undefined ? item.score : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
